import { DataGrid, GridColDef, GridFilterModel } from "@mui/x-data-grid";
import {
  PurchaseOrder,
  PurchaseOrderLineItem,
  PurchaseOrderLineItemReceipt,
  PurchaseOrderState,
} from "../types/api";
import { FC, useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CircleTwoToneIcon from "@mui/icons-material/CircleTwoTone";

import { useSearchParams } from "react-router-dom";
import StateChip from "./state-chip.component";
import ButtonWithConfirm from "./modal.confirm-dialog.component";
import CustomGridPagination from "./custom-pagination-actions";
import {
  Button,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { PURCHASING_API } from "../config";
import { currencyFormatter, useStickyState } from "../util";
import { dataTables } from "../styles";

interface Props {
  data: PurchaseOrder[] | undefined | null | any;
  pageSize: number;
  totalRows: number;
  currentPage: number;
  onSelectRow: Function;
  onPageChange: Function;
  onPageSizeChange: Function;
  onSortModelChange: Function;
  onFilterModelChange: Function;
  onDelete: Function;
  loading?: boolean;
}

const PurchaseOrderTable: FC<Props> = (props: Props) => {
  // Possible to redirect to this page with some params
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();

  const deletePurchaseOrder = (guid) => {
    props.onDelete(guid);
  };

  const filterModel: GridFilterModel = {
    items: [
      {
        field: "supplier",
        operator: "startsWith",
        value: searchParams.get("supplier"),
      },
    ],
  };

  const safeData = Array.isArray(props.data) ? props.data : [];
  const guidMap: Map<string, PurchaseOrder> = safeData.reduce(
    (map: Map<string, PurchaseOrder>, p: PurchaseOrder) => {
      map.set(p.guid!, p);
      return map;
    },
    new Map<string, PurchaseOrder>()
  );

  const columns: GridColDef[] = [
    { field: "id" },
    { field: "guid" },
    { field: "number", headerName: "PO Number", minWidth: 100, flex: 1 },
    {
      field: "state",
      headerName: "Status",
      minWidth: 100,
      flex: 1,
      align: "center",
      renderCell: (params) => <StateChip state={params.value} />,
    },
    {
      field: "receiveStatus",
      headerName: "Received",
      minWidth: 100,
      flex: 1,
      align: "center",
      renderCell: (params) => {
        const po: PurchaseOrder = params.value;

        const quantityOrdered = po.lineItems
          .map((i: PurchaseOrderLineItem) => i.quantityOrdered)
          .reduce((a, b) => a + b, 0);

        const qtr = quantityOrdered -
          po.lineItems
          .map((poli: PurchaseOrderLineItem) => poli.receipts)
          .reduce((a, b) => [...a, ...b], [])
          .map((r: PurchaseOrderLineItemReceipt) => r.received)
          .reduce((a, b) => a + b, 0);

        if (po.state === PurchaseOrderState.RECEIVED) {
          return <CircleIcon />;
        } else if (
          quantityOrdered === qtr
        ) {
          return <CircleOutlinedIcon />;
        } else {
          return <CircleTwoToneIcon />;
        }
      },
    },
    {
      field: "numLineItems",
      headerName: "Quantity",
      sortable: false,
      filterable: false,
      minWidth: 100,
      flex: 1,
      align: "left",
    },
    {
      field: "supplier",
      headerName: "Company Name",
      minWidth: 250,
      flex: 1,
    },
    {
      field: "totalCost",
      headerName: "Total Cost",
      minWidth: 100,
      flex: 1,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "normal", color: "green", width: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
          {params.value}
        </Typography>
      ),
    },
    { field: "issueDate", headerName: "Created", minWidth: 150, flex: 1 },
    {
      field: "buttons",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          {params.value.state !== PurchaseOrderState.DRAFT && (
            <Tooltip title="PDF">
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(
                    `${PURCHASING_API}pdf/${params.value.number}`,
                    "poWindow"
                  );
                }}
              >
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
          )}
          {(params.value.state === PurchaseOrderState.DRAFT || params.value.state === PurchaseOrderState.ACTIVE) && (
            <div onClick={(event) => event.stopPropagation()}>
              <ButtonWithConfirm
                variant="contained"
                message={
                  params.value.state === PurchaseOrderState.DRAFT
                    ? `This will permanently delete Purchase Order ${params.value.number}!`
                    : `This will void Purchase Order ${params.value.number}!`
                }
                color="primary"
                renderAsIcon
                onOk={() => deletePurchaseOrder(params.value.guid)}
                okButtonLabel={params.value.state === PurchaseOrderState.DRAFT ? "Delete" : "Void"}
              >
                <Tooltip title={params.value.state === PurchaseOrderState.DRAFT ? "Delete" : "Void"}>
                  <DeleteIcon />
                </Tooltip>
              </ButtonWithConfirm>
            </div>
          )}
        </>
      ),
    },
  ];

  const rows = safeData.map((p: PurchaseOrder) => {
    return {
      id: p.id,
      guid: p.guid,
      supplier: p.supplier?.name ?? '—',
      number: p.number,
      state: p.state,
      issueDate: p.issueDate
        ? new Date(p.issueDate).toLocaleDateString("en-GB", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "",
      numLineItems: p.lineItems
        .map((i: PurchaseOrderLineItem) => i.quantityOrdered)
        .reduce((a, b) => a + b, 0),
      receiveStatus: p,
      totalCost: currencyFormatter.format(p.total / 100),
      buttons: p,
    };
  });

  const filterButtons = [
    {
      label: "Active",
      model: {
        field: "state",
        operator: "equals",
        value: "active",
      },
    },
    { label: "All", model: undefined },
    {
      label: "Draft",
      model: {
        field: "state",
        operator: "equals",
        value: "draft",
      },
    },
    {
      label: "Void",
      model: {
        field: "state",
        operator: "equals",
        value: "void",
      },
    },
  ];

  const [selectedFilter, setSelectedFilter] = useStickyState<any>(
    filterButtons[0],
    "po.filterButtons"
  );

  useEffect(() => {
    props.onFilterModelChange(
      selectedFilter.model ? [selectedFilter.model] : undefined
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  return (
    <Paper
      component="form"
      sx={{ p: "2px 4px", display: "flex", alignItems: "center" }}
    >
      <Grid container>
        {filterButtons.map((b) => {
          const sx =
            b.label === selectedFilter.label
              ? { borderBottom: "2px solid rgb(186, 34, 36)" }
              : {};
          return (
            <Typography sx={sx}>
              <Button
                size="small"
                variant="text"
                onClick={() => setSelectedFilter(b)}
              >
                {b.label}
              </Button>
            </Typography>
          );
        })}
        <Grid size={12}>
          <DataGrid
            loading={props.loading || false}
            rows={rows}
            columns={columns}
            paginationModel={{ page: props.currentPage, pageSize: props.pageSize }}
            rowCount={props.totalRows}
            paginationMode="server"
            sortingMode="server"
            filterMode="server"
            autoHeight
            density="compact"
            onPaginationModelChange={(model) => {
              props.onPageChange(model.page);
              props.onPageSizeChange(model.pageSize);
            }}
            columnVisibilityModel={{
              id: false,
              guid: false
            }}
            onSortModelChange={(sm) => props.onSortModelChange(sm)}
            onFilterModelChange={(fm) => props.onFilterModelChange(fm)}
            onRowClick={(params) => {
              const po = guidMap.get(params.row.guid);
              if (po) {
                props.onSelectRow(po);
              } else {
                console.error('Purchase Order not found for guid:', params.row.guid);
              }
            }}
            initialState={{
              filter: {
                filterModel: filterModel,
              },
            }}
            slots={{
              pagination: CustomGridPagination,
            }}
            sx={dataTables}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PurchaseOrderTable;
